import React, { useCallback, useMemo, useState } from "react";
import moment from "moment";
import { getBirthdaysThisMonth } from "../../../../app/utils/common/dashboardEvents";
import { sendBirthdayEmail } from "../../../../app/utils/common/birthdayEmail";
import { showErrorResponse } from "../../../../app/utils/common/Functions";
import { showSuccessToast } from "../../../../app/utils/common/Toast";
import DashboardWidgetProfilePhoto from "../DashboardWidgetProfilePhoto";

const UpcomingBirthdays = ({
  employees = [],
  canSendCelebrationEmail = false,
  sentBirthdayEmailUserIds = [],
  onBirthdayEmailSent,
}) => {
  const [sendingUserId, setSendingUserId] = useState(null);
  const today = moment().startOf("day");

  const monthLabel = useMemo(() => moment().format("MMMM YYYY"), []);

  const items = useMemo(() => getBirthdaysThisMonth(employees), [employees]);

  const sentIdSet = useMemo(
    () => new Set(sentBirthdayEmailUserIds.map((id) => Number(id))),
    [sentBirthdayEmailUserIds]
  );

  const handleSendBirthdayEmail = useCallback(
    async (userId) => {
      if (!userId || sendingUserId || sentIdSet.has(Number(userId))) return;
      setSendingUserId(userId);
      try {
        const res = await sendBirthdayEmail(userId);
        showSuccessToast(res?.message || "Birthday email sent.");
        if (typeof onBirthdayEmailSent === "function") {
          onBirthdayEmailSent(userId);
        }
      } catch (err) {
        showErrorResponse(err);
        const msg =
          err?.response?.data?.error?.message ??
          err?.response?.data?.message ??
          err?.message ??
          "";
        if (/already been sent/i.test(String(msg)) && typeof onBirthdayEmailSent === "function") {
          onBirthdayEmailSent(userId);
        }
      } finally {
        setSendingUserId(null);
      }
    },
    [sendingUserId, sentIdSet, onBirthdayEmailSent]
  );

  return (
    <div className="dashboard-widget dashboard-widget--birthdays">
      <div className="dashboard-widget-header">
        <div className="dashboard-widget-header-left">
          <span className="dashboard-widget-icon-wrap">
            <img src="/svg/Icons/profile-icon.svg" alt="" aria-hidden="true" />
          </span>
          <h4 className="dashboard-widget-title">Birthdays this month</h4>
        </div>
        <span className="dashboard-widget-meta">{monthLabel}</span>
      </div>
      <div className="dashboard-widget-body">
        {items.length === 0 ? (
          <div className="dashboard-widget-empty">
            <span className="dashboard-widget-empty-icon dashboard-widget-empty-icon--birthday" aria-hidden="true">
              🎂
            </span>
            <p className="dashboard-widget-empty-text">No birthdays in {monthLabel}</p>
            <p className="dashboard-widget-empty-hint">Check back later for upcoming celebrations</p>
          </div>
        ) : (
          <ul className="dashboard-widget-list">
            {items.map((row) => {
              const isToday = row.nextDate.isSame(today, "day");
              return (
                <li
                  key={`bd-${row.userId}-${row.nextDate.format("YYYY-MM-DD")}`}
                  className="dashboard-widget-list-item"
                >
                  <DashboardWidgetProfilePhoto profileImage={row.profileImage} name={row.name} />
                  <div className="dashboard-widget-item-main">
                    <div className="dashboard-widget-item-name">{row.name}</div>
                    {isToday ? (
                      <div className="dashboard-widget-item-sub dashboard-widget-item-sub--today">Today</div>
                    ) : row.isPast ? (
                      <div className="dashboard-widget-item-sub">Completed</div>
                    ) : null}
                  </div>
                  <div className="dashboard-widget-item-end">
                    <span className="dashboard-widget-date">
                      {row.nextDate.format("ddd, DD MMM")}
                    </span>
                    {canSendCelebrationEmail && isToday && !sentIdSet.has(Number(row.userId)) ? (
                      <button
                        type="button"
                        className="dashboard-widget-celebrate-btn dashboard-widget-celebrate-btn--birthday"
                        disabled={sendingUserId === row.userId}
                        onClick={() => handleSendBirthdayEmail(row.userId)}
                      >
                        {sendingUserId === row.userId ? "Sending…" : "Send email"}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default UpcomingBirthdays;
